/* ============================================================
   storage — dashboard.

   What everyone needs to know before they trust a drive with
   something: is it there, how full is it, and is the disk
   itself healthy. Facts only — the numbers come from statvfs,
   the DB and SMART, and a number we cannot read says so rather
   than showing a reassuring zero.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icon, fmtBytes, fmtDate } from './lib';
import { api } from './api';

const pct = (used, cap) => (cap > 0 ? Math.min(100, (used / cap) * 100) : 0);

function HealthLine({ drive }) {
  const s = drive.smart || {};
  if (!drive.online && drive.setup_needed) {
    return <div className="st-health"><Icon name="info" size={14}/> Not set up yet <span className="st-health-why">{s.reason}</span></div>;
  }
  if (!drive.online) {
    return <div className="st-health st-health--bad"><Icon name="warn" size={14}/> Offline — nothing can be read from or written to it</div>;
  }
  if (!s.available) {
    return <div className="st-health"><Icon name="shield" size={14}/> Disk self-check unavailable <span className="st-health-why">{s.reason}</span></div>;
  }
  const bad = !s.passed || (s.pending || 0) > 0 || (s.reallocated || 0) > 0;
  return (
    <div className={`st-health ${bad ? 'st-health--bad' : 'st-health--ok'}`}>
      <Icon name="shield" size={14}/>
      {s.passed ? 'Disk self-check passed' : 'Disk self-check FAILED'}
      {(s.pending || 0) > 0 && <span className="st-health-why">{s.pending} sectors pending</span>}
      {(s.reallocated || 0) > 0 && <span className="st-health-why">{s.reallocated} reallocated</span>}
    </div>
  );
}

function DriveCard({ drive, onOpen }) {
  const used = pct(drive.used, drive.cap);
  const level = used > 90 ? 'err' : used > 75 ? 'warn' : '';
  const s = drive.smart || {};
  return (
    <div className="panel st-drive">
      <div className="panel-head st-drive-head">
        <span className="st-drive-name">
          <Icon name="drive" size={16}/>
          <span>{drive.label}</span>
        </span>
        <span className="st-drive-state">
          <span className={`dot ${drive.online ? 'dot--ok' : 'dot--err'}`} aria-hidden="true"/>
          <span className="meta">{drive.online ? 'online' : 'offline'}</span>
        </span>
        <button type="button" className="btn btn--ghost btn--sm st-drive-browse"
                disabled={!drive.online} onClick={() => onOpen(drive.id)}>
          Open
        </button>
      </div>

      <div className="st-drive-body">
        <div className="st-drive-figures">
          <div>
            <div className="st-drive-used">{fmtBytes(drive.used)}</div>
            <div className="st-drive-cap">of {fmtBytes(drive.cap)} allowance</div>
          </div>
          <div className="st-drive-right">
            <div className="st-drive-count">{drive.files.toLocaleString()}</div>
            <div className="st-drive-cap">files</div>
          </div>
        </div>

        <div className="bar st-drivebar-big" role="img" aria-label={`${Math.round(used)} percent of the allowance used`}>
          <div className={`fill ${level ? `fill--${level}` : ''}`} style={{ width: `${Math.max(used, 0.5)}%` }}/>
        </div>

        <dl className="st-facts">
          <div><dt>Free to use</dt><dd>{drive.online ? fmtBytes(drive.free) : '—'}</dd></div>
          <div><dt>Disk free</dt><dd>{drive.disk_free != null ? fmtBytes(drive.disk_free) : '—'}</dd></div>
          <div><dt>Disk size</dt><dd>{drive.disk_total != null ? fmtBytes(drive.disk_total) : '—'}</dd></div>
          <div><dt>Folders</dt><dd>{drive.folders.toLocaleString()}</dd></div>
          {drive.trash_cap != null && (
            <div title={`Cleared after ${drive.trash_days} days, or oldest first past ${fmtBytes(drive.trash_cap)}`}>
              <dt>In the trash</dt><dd>{fmtBytes(drive.trash_used)} of {fmtBytes(drive.trash_cap)}</dd>
            </div>
          )}
          <div><dt>Machine</dt><dd>{drive.host}</dd></div>
          <div><dt>Filesystem errors</dt><dd>{drive.fs_errors == null ? '—' : drive.fs_errors}</dd></div>
          {s.temperature_c != null && <div><dt>Temperature</dt><dd>{s.temperature_c}°C</dd></div>}
          {s.power_on_hours != null && (
            <div><dt>Powered on</dt><dd>{Math.round(s.power_on_hours / 24).toLocaleString()} days</dd></div>
          )}
          {s.model && <div><dt>Disk</dt><dd title={s.model}>{s.model}</dd></div>}
        </dl>

        <HealthLine drive={drive}/>
      </div>
    </div>
  );
}

/* What the server keeps beside the files, for admins: streaming copies and
   the trim cache. Neither counts towards a drive's allowance, and both are
   cleared on their own — this is where to see how full they are anyway. */
function Caches({ token }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    Promise.all([api.adminStreams(token).catch(() => null), api.adminTrim(token).catch(() => null)])
      .then(([streams, trim]) => { if (live) setD({ streams, trim }); });
    return () => { live = false; };
  }, [token]);
  if (!d || (!d.streams && !d.trim)) return null;
  const { streams, trim } = d;
  const bs = trim?.by_status || {};
  return (
    <div className="panel st-sum">
      <div className="panel-head"><span className="label">Caches · outside the allowances</span></div>
      <div className="st-drive-body">
        {streams && (
          <>
            <div className="label">Streaming copies — {fmtBytes(streams.cache_bytes)} of {fmtBytes(streams.cache_cap)}</div>
            <div className="bar"><div className="fill" style={{ width: `${pct(streams.cache_bytes, streams.cache_cap)}%` }}/></div>
          </>
        )}
        {trim && (
          <>
            <div className="label">Trim cache — {fmtBytes(trim.cache_bytes)} of {fmtBytes(trim.cache_cap)}</div>
            <div className="bar"><div className="fill" style={{ width: `${pct(trim.cache_bytes, trim.cache_cap)}%` }}/></div>
          </>
        )}
        <dl className="st-facts">
          {streams && <div><dt>Videos with copies</dt><dd>{(streams.by_status?.ready || 0).toLocaleString()} of {streams.videos.toLocaleString()}</dd></div>}
          {streams?.working && <div><dt>Encoding</dt><dd title={streams.working.original_name}>{Math.round(streams.working.progress * 100)}% · {streams.working.original_name}</dd></div>}
          {trim && <div><dt>Trims running</dt><dd>{(bs.working || 0)} · {(bs.queued || 0)} waiting</dd></div>}
          {trim && <div><dt>Results unsaved</dt><dd>{bs.ready || 0}</dd></div>}
          {trim && <div><dt>Originals kept for undo</dt><dd>{trim.undo.count} · {fmtBytes(trim.undo.bytes)}</dd></div>}
        </dl>
      </div>
    </div>
  );
}

/* The shape of a drive card with nothing in it yet, so the page does not
   jump from empty to full when the first answer arrives. */
function DriveCardSkeleton() {
  return (
    <div className="panel st-drive st-drive--skel" aria-hidden="true">
      <div className="panel-head st-drive-head">
        <span className="skeleton st-sk st-sk--name"/>
        <span className="skeleton st-sk st-sk--chip"/>
      </div>
      <div className="st-drive-body">
        <div className="st-drive-figures">
          <div><span className="skeleton st-sk st-sk--big"/><span className="skeleton st-sk st-sk--line"/></div>
          <div className="st-drive-right"><span className="skeleton st-sk st-sk--num"/><span className="skeleton st-sk st-sk--short"/></div>
        </div>
        <span className="skeleton st-sk st-sk--bar"/>
        <span className="skeleton st-sk st-sk--line"/>
        <span className="skeleton st-sk st-sk--line st-sk--w70"/>
      </div>
    </div>
  );
}

export default function Dashboard({ drives, quota, recent, me, loading, onOpenDrive, onRefresh, token }) {
  const first = loading && !drives.length;
  const totalUsed = drives.reduce((n, d) => n + d.used, 0);
  const totalCap = drives.filter((d) => !d.setup_needed).reduce((n, d) => n + d.cap, 0);
  const offline = drives.filter((d) => !d.online && !d.setup_needed);
  const unready = drives.filter((d) => d.setup_needed);

  return (
    <div className="st-dash">
      <div className="st-dash-head">
        <h2 className="h2">Drives</h2>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRefresh} disabled={loading}>
          <Icon name="refresh" size={14}/> {loading ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {offline.length > 0 && (
        <div className="alert alert--warn st-alert">
          <strong>{offline.map((d) => d.label).join(' and ')} {offline.length > 1 ? 'are' : 'is'} offline.</strong>{' '}
          Files stored there cannot be opened until the drive is back. Everything on the other drives is unaffected.
        </div>
      )}

      {unready.length > 0 && (
        <div className="alert alert--info st-alert">
          <strong>{unready.map((d) => d.label).join(' and ')} {unready.length > 1 ? 'are' : 'is'} not set up yet.</strong>{' '}
          {me?.role === 'admin'
            ? <>It needs one command as root on disinteg: <code>sudo bash /home/disinteg/tegtech/setup-root-drive.sh</code></>
            : 'It will appear here once an admin has set it up.'}
        </div>
      )}

      <div className="st-dash-grid" aria-busy={first}>
        {first
          ? [0, 1, 2].map((i) => <DriveCardSkeleton key={i}/>)
          : drives.map((d) => <DriveCard key={d.id} drive={d} onOpen={onOpenDrive}/>)}
      </div>

      <div className="st-dash-grid st-dash-grid--split">
        <div className="panel st-sum">
          <div className="panel-head"><span className="label">Across all drives</span></div>
          <div className="st-drive-body">
            {first ? (
              <div className="st-sk-stack" aria-hidden="true">
                <span className="skeleton st-sk st-sk--big"/>
                <span className="skeleton st-sk st-sk--short"/>
                <span className="skeleton st-sk st-sk--bar"/>
                {[0, 1, 2, 3, 4].map((i) => <span key={i} className="skeleton st-sk st-sk--line"/>)}
              </div>
            ) : (<>
            <div className="value">{fmtBytes(totalUsed)}</div>
            <div className="label">used of {fmtBytes(totalCap)}</div>
            <div className="bar"><div className="fill" style={{ width: `${pct(totalUsed, totalCap)}%` }}/></div>
            <dl className="st-facts">
              <div><dt>Your files</dt><dd>{fmtBytes(quota?.my_used || 0)}</dd></div>
              <div><dt>Largest single file</dt><dd>{fmtBytes(quota?.max_file || 0)}</dd></div>
              {drives.map((d) => (
                <div key={d.id}>
                  <dt>On {d.label.replace(' drive', '')}</dt>
                  <dd>{fmtBytes(d.used)}{d.online ? '' : ' (offline)'}</dd>
                </div>
              ))}
              <div><dt>Files stored</dt><dd>{drives.reduce((n, d) => n + d.files, 0).toLocaleString()}</dd></div>
            </dl>
            </>)}
          </div>
        </div>

        {me?.role === 'admin' && token && <Caches token={token}/>}

        <div className="panel st-recent">
          <div className="panel-head"><span className="label">Recently added</span></div>
          {recent === null && (first || drives.length > 0) ? (
            <ul className="st-recent-list" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <li key={i} className="listrow st-recent-row">
                  <span className="skeleton st-sk st-sk--line"/>
                  <span className="skeleton st-sk st-sk--short"/>
                  <span className="skeleton st-sk st-sk--short"/>
                  <span className="skeleton st-sk st-sk--short"/>
                </li>
              ))}
            </ul>
          ) : !recent?.length ? (
            <div className="empty st-empty-sm">Nothing uploaded yet.</div>
          ) : (
            <ul className="st-recent-list">
              {recent.slice(0, 7).map((f) => (
                <li key={f.id} className="listrow st-recent-row">
                  <span className="st-recent-name" title={f.original_name}>{f.original_name}</span>
                  <span className="meta">{f.owner_name === me?.username ? 'you' : f.owner_name}</span>
                  <span className="tnum st-recent-size">{fmtBytes(f.size)}</span>
                  <span className="meta">{fmtDate(f.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
